/**
 * Table ⇄ Analytics switch for a report header.
 *
 * A segmented control rather than two buttons: the two views are alternatives
 * over the SAME result set — one filter run feeds both — and a segment makes
 * that mutual exclusivity obvious. Rendered as a tablist so the arrow keys and
 * the screen-reader announcement come for free.
 */
import type { JSX } from 'react'
import { LayoutGrid, Table2 } from 'lucide-react'

export type ReportView = 'table' | 'analytics'

const TABS: { id: ReportView; label: string; Icon: typeof Table2 }[] = [
  { id: 'table', label: 'Table', Icon: Table2 },
  { id: 'analytics', label: 'Analytics', Icon: LayoutGrid },
]

export default function ReportViewTabs({
  value,
  onChange,
  disabled = false,
}: {
  value: ReportView
  onChange: (next: ReportView) => void
  /** True until the report has been run — there is nothing to switch between yet. */
  disabled?: boolean
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Report view"
      className="flex h-8 items-center gap-0.5 rounded-[3px] border border-slate-200 bg-slate-50 p-0.5"
    >
      {TABS.map(({ id, label, Icon }) => {
        const active = value === id
        return (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(id)}
            className={`flex h-7 items-center gap-1.5 rounded-[2px] px-2.5 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              active
                ? 'bg-white text-[#3366FF] shadow-sm'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Icon className={`h-3.5 w-3.5 ${active ? 'text-[#3366FF]' : 'text-slate-400'}`} />
            {label}
          </button>
        )
      })}
    </div>
  )
}
